/**
 * Lead Submission Validation
 *
 * Zod schema validating all waitlist form fields.
 * Used in:
 *   - /api/widget/submit  (server-side — blocks malformed data from reaching DB)
 *   - EmailSignupSection  (client-side — instant inline per-field feedback)
 */

import { z } from 'zod';

// Accepts common international phone formats:
//   +1-555-123-4567 | (555) 123-4567 | +44 7911 123456 | 555.123.4567
const PHONE_REGEX = /^[+]?[\d\s\-()\/.]{7,25}$/;

// Blocks basic HTML tag injection (<script>, <img>, etc.)
const noHtmlTags = (val: string) => !/<[^>]*>/.test(val);

// RFC 5321 compliant email regex (stricter than the simple /@/ check)
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

export const leadSubmitSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .max(254, 'Email address is too long')
    .regex(EMAIL_REGEX, 'Please enter a valid email address'),

  firstName: z
    .string()
    .max(100, 'First name is too long (max 100 characters)')
    .refine(noHtmlTags, 'First name contains invalid characters')
    .optional(),

  lastName: z
    .string()
    .max(100, 'Last name is too long (max 100 characters)')
    .refine(noHtmlTags, 'Last name contains invalid characters')
    .optional(),

  phone: z
    .string()
    .refine(
      (val) => !val || PHONE_REGEX.test(val),
      'Please enter a valid phone number (e.g. +1-555-123-4567)'
    )
    .optional(),

  customAnswer: z
    .string()
    .max(500, 'Response is too long (max 500 characters)')
    .refine(noHtmlTags, 'Response contains invalid characters')
    .optional(),
});

export type LeadSubmitInput = z.infer<typeof leadSubmitSchema>;
export type LeadFieldErrors = Partial<Record<keyof LeadSubmitInput, string>>;
